import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    X, AlertTriangle, CheckCircle2, ShieldCheck, MessageSquare, Plus,
    ArrowRight, Loader2, ChevronDown, ChevronRight, FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError, normaliseApiError } from "@/lib/apiError";

/**
 * DEV-010C · Operations survey Review drawer.
 *
 * Opens over the Operations project workspace when Ops clicks
 * "Review Survey" on a submitted survey. Presents:
 *   · Summary strip (submitted at / surveyor / linked visit / sign-off)
 *   · Variance list (proposed vs surveyed, with Accept/Retain/Specify)
 *   · Manual Review Items (Ops can add a non-comparable finding)
 *   · Clarification history + Return-for-clarification action
 *   · Approve gate with explicit blockers
 *
 * All mutations hit the DEV-010C endpoints on `/api/surveys/{id}/...`;
 * this component holds NO business rules — the backend is authoritative.
 */

const DECISION_LABEL = {
    accept_survey: "Accept survey finding",
    retain_proposed: "Retain proposed value",
    specify_value: "Specify approved value",
};

function fmt(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
}

function valueDisplay(v) {
    if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
    if (v === null || v === undefined || v === "") return "—";
    return String(v);
}

export default function OperationsSurveyReview({ surveyId, onClose, onLifecycleChange, onProjectChanged }) {
    const [review, setReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [showReturn, setShowReturn] = useState(false);
    const [returnText, setReturnText] = useState("");
    // DEV-010H · Multi-target clarification builder. Each target pins the
    // clarification to a specific section/field/elevation so the Engineer
    // knows exactly what to fix.
    const [returnTargets, setReturnTargets] = useState([]);
    const [showApprove, setShowApprove] = useState(false);
    const [approveNote, setApproveNote] = useState("");
    // DEV-010H UAT corrective pass · Explicit "Edit survey" mode. When
    // off, findings are read-only. When on, we expose the full-edit
    // OpsEditPanel prominently at the top of the review body so the
    // operator can amend any canonical survey field.
    const [editMode, setEditMode] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            setReview(await api.getSurveyReview(surveyId));
        } catch (e) {
            setError(formatApiError(e));
        } finally { setLoading(false); }
    }, [surveyId]);

    useEffect(() => { load(); }, [load]);

    const runDecision = async (key, decision, approvedValue, note) => {
        setBusy(key);
        try {
            await api.decideVariance(surveyId, {
                key, decision, approved_value: approvedValue, note,
            });
            await load();
        } catch (e) {
            alert(formatApiError(e));
        } finally { setBusy(null); }
    };

    const returnSurvey = async () => {
        if (!returnText.trim()) return;
        setBusy("return");
        try {
            await api.returnSurvey(surveyId, {
                text: returnText,
                // DEV-010H · Send targets whenever the operator picked any.
                targets: returnTargets.length ? returnTargets : undefined,
            });
            await load();
            setShowReturn(false); setReturnText(""); setReturnTargets([]);
            onLifecycleChange?.();
            // WF-001A2C · Return-for-clarification is a canonical Ops
            // action on the survey lifecycle. Route through the shared
            // parent refresh so the project snapshot (task rows, phase
            // counts, outstanding total, source metadata) picks up any
            // downstream reconciliation in the same rendered view.
            // Safe no-op when the parent hasn't wired the callback.
            if (typeof onProjectChanged === "function") {
                await Promise.resolve(onProjectChanged());
            }
        } catch (e) {
            alert(formatApiError(e));
        } finally { setBusy(null); }
    };

    const approveSurvey = async () => {
        setBusy("approve");
        try {
            await api.approveSurvey(surveyId, { note: approveNote || null });
            await load();
            setShowApprove(false); setApproveNote("");
            onLifecycleChange?.();
            // WF-001A2C · Approval fires `sync_survey_approved` on the
            // backend which auto-completes canonical tasks (e.g.
            // `site_survey.approved`). Refresh the shared project
            // snapshot exactly once so the same rendered view reflects
            // the updated task statuses, phase counts, outstanding
            // total and progress — no polling / no browser reload.
            if (typeof onProjectChanged === "function") {
                await Promise.resolve(onProjectChanged());
            }
        } catch (e) {
            const n = normaliseApiError(e);
            // Approval blocker responses carry their own blocker list — show
            // it beneath the primary message instead of stringifying the raw
            // object (which produced "[object Object]" in the previous UAT).
            const blockers = Array.isArray(e?.response?.data?.detail?.blockers)
                ? e.response.data.detail.blockers.join("\n")
                : "";
            alert(blockers ? `${n.message}\n${blockers}` : n.message);
        } finally { setBusy(null); }
    };

    const addReviewItem = async () => {
        const desc = window.prompt("Describe the site finding requiring Ops decision:");
        if (!desc?.trim()) return;
        setBusy("addItem");
        try {
            await api.createReviewItem(surveyId, { description: desc });
            await load();
        } catch (e) {
            alert(formatApiError(e));
        } finally { setBusy(null); }
    };

    const decideItem = async (item, decision) => {
        const note = window.prompt("Add a note (optional):", "") || "";
        setBusy(item.id);
        try {
            await api.decideReviewItem(surveyId, item.id, {
                description: item.description, decision, note,
            });
            await load();
        } catch (e) {
            alert(formatApiError(e));
        } finally { setBusy(null); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" data-testid="ops-survey-review">
            <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl">
                <header className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.12em] text-[#4B5565] font-medium">Operations review</div>
                        <h2 className="text-xl text-[#131B2F] font-medium">Site survey review</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* DEV-010H UAT corrective pass · Prominent Edit toggle.
                            Operations has full authority to amend any canonical
                            survey field. Toggle reveals the full-edit surface
                            in-place; every change writes to the audit trail
                            and (on approved surveys) requires explicit
                            re-approval. */}
                        <button
                            type="button"
                            onClick={() => setEditMode((v) => !v)}
                            data-testid="ops-edit-toggle"
                            aria-pressed={editMode}
                            className={"inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium border transition-colors " +
                                (editMode
                                    ? "bg-[#131B2F] text-white border-[#131B2F]"
                                    : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F9FAFB]")}
                        >
                            {editMode ? "Exit editing" : "Edit survey"}
                        </button>
                        <button
                            onClick={onClose}
                            data-testid="review-close"
                            className="w-9 h-9 rounded-full text-[#4B5565] hover:bg-[#F1F5F9] flex items-center justify-center"
                        >
                            <X size={18} strokeWidth={1.75} />
                        </button>
                    </div>
                </header>

                {loading ? (
                    <div className="p-8 text-sm text-[#4B5565] text-center">
                        <Loader2 size={16} className="inline-block animate-spin mr-2" /> Loading…
                    </div>
                ) : error ? (
                    <div className="p-8 text-sm text-[#B91C1C]">{error}</div>
                ) : (
                    // DEV-010H UAT corrective pass · pb-24 clears the sticky
                    // Return/Approve action bar so the tail of the drawer
                    // (OpsEditPanel Save, OpsAuditPanel) is never overlaid.
                    <div className="p-6 pb-24 space-y-6">
                        {/* DEV-010E · Approved state banner. Once approval
                            has succeeded the survey is terminal — the
                            operator sees an unambiguous success card, not
                            the submitted-state "Approval blocked" gate. */}
                        {review.is_approved ? (
                            <div className="rounded-xl border p-4 border-[#DCFCE7] bg-[#F1F8EC]" data-testid="approval-status">
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 size={20} className="text-[#166534] mt-0.5" strokeWidth={1.75} />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-[#131B2F]">Survey approved</div>
                                        <div className="text-xs text-[#4B5565] mt-1 space-y-0.5">
                                            {review.approval?.approved_by_name && (
                                                <div>By <b className="text-[#131B2F]">{review.approval.approved_by_name}</b></div>
                                            )}
                                            {review.approval?.approved_at && (
                                                <div>Approved {fmt(review.approval.approved_at)}</div>
                                            )}
                                            {review.approval?.approval_note && (
                                                <div className="mt-1">Note: {review.approval.approval_note}</div>
                                            )}
                                            {review.approved_specification && (
                                                <div className="mt-1 flex items-center gap-1.5 text-[#166534]">
                                                    <FileText size={12} strokeWidth={2} />
                                                    Approved Installation Specification · Version {review.approved_specification.version}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Approval blockers — only relevant while the
                               survey is still in submitted state. */
                            <div
                                className={`rounded-xl border p-4 ${
                                    review.can_approve
                                        ? "border-[#DCFCE7] bg-[#F1F8EC]"
                                        : "border-[#FEE2E2] bg-[#FEF2F2]"
                                }`}
                                data-testid="approval-status"
                            >
                                <div className="flex items-start gap-3">
                                    {review.can_approve ? (
                                        <CheckCircle2 size={20} className="text-[#166534] mt-0.5" strokeWidth={1.75} />
                                    ) : (
                                        <AlertTriangle size={20} className="text-[#B91C1C] mt-0.5" strokeWidth={1.75} />
                                    )}
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-[#131B2F]">
                                            {review.can_approve
                                                ? "Ready for approval"
                                                : `Approval blocked — ${review.approval_blockers.length} issue${review.approval_blockers.length === 1 ? "" : "s"} outstanding`}
                                        </div>
                                        {!review.can_approve && (
                                            <ul className="text-xs text-[#B91C1C] mt-1 list-disc list-inside space-y-0.5" data-testid="approval-blockers">
                                                {review.approval_blockers.map((b, i) => <li key={i}>{b}</li>)}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* DEV-010E · Survey Findings.
                            The authoritative read-only view of the
                            Engineer's submitted survey — Additional
                            options, Electrical, Roof/elevations, Panel
                            installation, Cable & conduit, Equipment
                            location, Additional notes, Photographs +
                            Sign-off — so Operations can review the work
                            without leaving the drawer. */}
                        <SurveyFindings review={review} editMode={editMode} />
                        {/* Variances */}
                        <section>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-base font-medium text-[#131B2F]">
                                    Variances ({review.variances.filter(v => v.differs).length} require review)
                                </h3>
                            </div>
                            {review.variances.length === 0 && (
                                <div className="text-sm text-[#4B5565] p-3 rounded-lg bg-[#FAFBFC] border border-dashed border-[#E5E7EB]">
                                    No structured comparisons available for this survey.
                                </div>
                            )}
                            <div className="space-y-2">
                                {review.variances.map((v) => (
                                    <VarianceRow key={v.key} variance={v} busy={busy === v.key} onDecide={runDecision} />
                                ))}
                            </div>
                        </section>

                        {/* Manual review items */}
                        <section>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-base font-medium text-[#131B2F]">Manual review items</h3>
                                <button
                                    onClick={addReviewItem}
                                    disabled={busy === "addItem"}
                                    data-testid="add-review-item"
                                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-medium bg-white border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F9FAFB]"
                                >
                                    <Plus size={12} strokeWidth={2} /> Add item
                                </button>
                            </div>
                            {(review.review_items || []).length === 0 ? (
                                <div className="text-sm text-[#4B5565] p-3 rounded-lg bg-[#FAFBFC] border border-dashed border-[#E5E7EB]">
                                    No manual review items. Add one if a survey finding needs an Ops decision but doesn&rsquo;t match a proposed value.
                                </div>
                            ) : (
                                <ul className="space-y-2" data-testid="review-items">
                                    {review.review_items.map((it) => (
                                        <li key={it.id} className="p-3 border border-[#E5E7EB] rounded-lg" data-testid={`review-item-${it.id}`}>
                                            <div className="text-sm text-[#131B2F]">{it.description}</div>
                                            <div className="text-[11px] text-[#4B5565] mt-1">
                                                Created by {it.created_by_name || "Ops"} · {fmt(it.created_at)}
                                            </div>
                                            {it.decision ? (
                                                <div className="mt-2 text-xs text-[#166534] font-medium">
                                                    ✓ {it.decision}{it.note ? ` — ${it.note}` : ""}
                                                </div>
                                            ) : (
                                                <div className="mt-2 flex gap-2">
                                                    <button
                                                        onClick={() => decideItem(it, "accepted")}
                                                        disabled={busy === it.id}
                                                        className="h-7 px-2.5 rounded-full text-xs bg-[#131B2F] text-white hover:brightness-110"
                                                    >
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={() => decideItem(it, "rejected")}
                                                        disabled={busy === it.id}
                                                        className="h-7 px-2.5 rounded-full text-xs border border-[#E5E7EB] text-[#131B2F] bg-white hover:bg-[#F9FAFB]"
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        {/* Clarification history */}
                        {(review.clarifications || []).length > 0 && (
                            <section>
                                <h3 className="text-base font-medium text-[#131B2F] mb-2">Clarification history</h3>
                                <ul className="space-y-2" data-testid="clarification-history">
                                    {review.clarifications.map((c) => (
                                        <li key={c.id} className="p-3 border border-[#E5E7EB] rounded-lg bg-[#FAFBFC]">
                                            <div className="flex items-start gap-2">
                                                <MessageSquare size={14} className="mt-0.5 text-[#4B5565]" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs text-[#4B5565]">
                                                        {c.requested_by_name || "Ops"} · {fmt(c.requested_at)}
                                                        {c.section && ` · ${c.section}`}
                                                    </div>
                                                    <div className="text-sm text-[#131B2F] mt-1">{c.text}</div>
                                                    {/* DEV-010H · Render every pinned
                                                        target so Ops (and, downstream,
                                                        the Engineer) can see exactly
                                                        which items were queried. */}
                                                    {(c.targets || []).length > 0 && (
                                                        <ul className="mt-2 flex flex-wrap gap-1" data-testid={`clar-targets-${c.id}`}>
                                                            {c.targets.map((t, i) => (
                                                                <li key={i}
                                                                    className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#1E3A8A] border border-[#C7D2FE]">
                                                                    {t.label || [t.section, t.field, t.elevation_id ? "elevation" : null].filter(Boolean).join(" · ")}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    {c.engineer_response && (
                                                        <div className="mt-2 pl-3 border-l-2 border-[#3E9E2E]">
                                                            <div className="text-xs text-[#4B5565]">
                                                                Engineer response · {fmt(c.resolved_at)}
                                                            </div>
                                                            <div className="text-sm text-[#131B2F]">{c.engineer_response}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Action bar — DEV-010E: hide Return/Approve
                            once the survey is approved. Read-only survey
                            findings + clarification history + variance
                            decisions remain visible for audit. */}
                        {!review.is_approved && (
                            <div className="sticky bottom-0 -mx-6 px-6 py-4 border-t border-[#E5E7EB] bg-white flex gap-2 justify-end">
                                <button
                                    onClick={() => setShowReturn(true)}
                                    disabled={review.survey.status !== "submitted"}
                                    data-testid="return-survey-btn"
                                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-medium bg-white border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F9FAFB] disabled:opacity-50"
                                >
                                    <ArrowRight size={14} strokeWidth={2} /> Return for clarification
                                </button>
                                <button
                                    onClick={() => setShowApprove(true)}
                                    disabled={!review.can_approve}
                                    data-testid="approve-survey-btn"
                                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-medium bg-[#3E9E2E] text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ShieldCheck size={14} strokeWidth={2} /> Approve survey
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Return-for-clarification modal */}
            {showReturn && (
                <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
                        <div className="text-lg font-medium text-[#131B2F]">Return for clarification</div>
                        <div className="text-sm text-[#4B5565] mt-1">Describe what the Engineer needs to clarify or amend.</div>
                        <textarea
                            value={returnText}
                            onChange={(e) => setReturnText(e.target.value)}
                            rows={4}
                            data-testid="return-text"
                            className="mt-3 w-full p-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#131B2F]"
                            placeholder="e.g. Please clarify roof access constraints on the east elevation."
                        />
                        {/* DEV-010H · Multi-target picker. Ops can pin the
                            clarification to specific sections, elevations,
                            or fields so the Engineer knows exactly what
                            needs attention. */}
                        <ClarificationTargetsPicker
                            targets={returnTargets}
                            onChange={setReturnTargets}
                            survey={review?.survey}
                            elevations={review?.elevations || []}
                        />
                        <div className="mt-4 flex gap-2 justify-end">
                            <button onClick={() => { setShowReturn(false); setReturnTargets([]); }} className="h-10 px-4 rounded-full text-sm border border-[#E5E7EB] bg-white text-[#131B2F]">Cancel</button>
                            <button
                                onClick={returnSurvey}
                                disabled={!returnText.trim() || busy === "return"}
                                data-testid="return-confirm"
                                className="h-10 px-4 rounded-full text-sm bg-[#131B2F] text-white disabled:opacity-50"
                            >
                                {busy === "return" ? "Returning…" : "Return survey"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Approve modal */}
            {showApprove && (
                <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                        <div className="text-lg font-medium text-[#131B2F]">Approve site survey</div>
                        <div className="text-sm text-[#4B5565] mt-1">This creates the Approved Installation Specification (Version 1) and locks the survey.</div>
                        <textarea
                            value={approveNote}
                            onChange={(e) => setApproveNote(e.target.value)}
                            rows={3}
                            data-testid="approve-note"
                            placeholder="Optional approval note"
                            className="mt-3 w-full p-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#131B2F]"
                        />
                        <div className="mt-4 flex gap-2 justify-end">
                            <button onClick={() => setShowApprove(false)} className="h-10 px-4 rounded-full text-sm border border-[#E5E7EB] bg-white text-[#131B2F]">Cancel</button>
                            <button
                                onClick={approveSurvey}
                                disabled={busy === "approve"}
                                data-testid="approve-confirm"
                                className="h-10 px-4 rounded-full text-sm bg-[#3E9E2E] text-white disabled:opacity-50"
                            >
                                {busy === "approve" ? "Approving…" : "Confirm approval"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function VarianceRow({ variance: v, busy, onDecide }) {
    const [specVal, setSpecVal] = useState("");
    const [note, setNote] = useState("");
    const [openSpec, setOpenSpec] = useState(false);
    const decided = v.decision;
    const barColour = !v.comparable ? "#94A3B8" : v.differs ? "#F59E0B" : "#3E9E2E";

    return (
        <div className="p-3 border border-[#E5E7EB] rounded-lg" data-testid={`variance-${v.key}`}>
            <div className="flex items-start gap-3">
                <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: barColour }} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-[#131B2F]">{v.label}</div>
                        <span className="text-[10px] uppercase tracking-[0.1em] font-medium text-[#4B5565]">{v.section}</span>
                        {!v.comparable && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#4B5565]">informational</span>}
                        {v.comparable && v.differs && !decided && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#B45309]">needs decision</span>}
                        {decided && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F1F8EC] text-[#166534]">decided</span>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.12em] text-[#4B5565]">Proposed</div>
                            <div className="text-[#131B2F] truncate" data-testid={`variance-${v.key}-proposed`}>{valueDisplay(v.proposed)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.12em] text-[#4B5565]">Survey finding</div>
                            <div className="text-[#131B2F] truncate" data-testid={`variance-${v.key}-survey`}>{valueDisplay(v.survey)}</div>
                        </div>
                    </div>
                    {decided ? (
                        <div className="mt-2 text-xs text-[#166534]">
                            ✓ {DECISION_LABEL[decided.decision]}. Approved: <b>{valueDisplay(decided.approved_value)}</b>
                            {decided.note && <div className="text-[#4B5565] mt-0.5">Note: {decided.note}</div>}
                        </div>
                    ) : v.comparable && v.differs ? (
                        <div className="mt-2 space-y-2">
                            <div className="flex flex-wrap gap-1.5">
                                <button
                                    onClick={() => onDecide(v.key, "accept_survey", v.survey, null)}
                                    disabled={busy}
                                    data-testid={`decide-${v.key}-accept`}
                                    className="h-8 px-3 rounded-full text-xs font-medium bg-[#131B2F] text-white hover:brightness-110"
                                >
                                    Accept survey
                                </button>
                                <button
                                    onClick={() => {
                                        const n = window.prompt(`Note required — why retain "${valueDisplay(v.proposed)}"?`);
                                        if (n?.trim()) onDecide(v.key, "retain_proposed", v.proposed, n);
                                    }}
                                    disabled={busy}
                                    data-testid={`decide-${v.key}-retain`}
                                    className="h-8 px-3 rounded-full text-xs font-medium bg-white border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F9FAFB]"
                                >
                                    Retain proposed
                                </button>
                                <button
                                    onClick={() => setOpenSpec(!openSpec)}
                                    disabled={busy}
                                    data-testid={`decide-${v.key}-specify`}
                                    className="h-8 px-3 rounded-full text-xs font-medium bg-white border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F9FAFB]"
                                >
                                    Specify value…
                                </button>
                            </div>
                            {openSpec && (
                                <div className="p-2 rounded-lg bg-[#FAFBFC] border border-[#E5E7EB] space-y-2">
                                    <input
                                        value={specVal}
                                        onChange={(e) => setSpecVal(e.target.value)}
                                        placeholder="Approved value"
                                        data-testid={`specify-${v.key}-value`}
                                        className="w-full p-2 border border-[#E5E7EB] rounded text-xs"
                                    />
                                    <input
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        placeholder="Reason (required)"
                                        data-testid={`specify-${v.key}-note`}
                                        className="w-full p-2 border border-[#E5E7EB] rounded text-xs"
                                    />
                                    <button
                                        onClick={() => {
                                            const val = typeof v.proposed === "number" ? Number(specVal) : specVal;
                                            onDecide(v.key, "specify_value", val, note);
                                            setOpenSpec(false); setSpecVal(""); setNote("");
                                        }}
                                        disabled={!specVal.trim() || !note.trim() || busy}
                                        data-testid={`specify-${v.key}-confirm`}
                                        className="h-7 px-3 rounded-full text-xs bg-[#131B2F] text-white disabled:opacity-50"
                                    >
                                        Save
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : v.comparable && !v.differs ? (
                        <div className="mt-1 text-[11px] text-[#166534]">Matches proposed value.</div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

/**
 * DEV-010E · Read-only Survey Findings for Operations review.
 *
 * Renders the Engineer's captured survey as a stack of collapsible
 * sub-sections aligned to the DEV-010B capture flow. All rendering is
 * safe when fields are missing — a submitted survey is guaranteed to
 * have sign-off, but individual sections may be skipped in edge cases.
 * Photographs render via the scoped `download_url` already minted by
 * the backend on `/api/surveys/{id}/review` — the operator does not
 * fetch documents through a separate permission surface.
 *
 * No editing controls — Ops preserves survey findings as-captured;
 * variance decisions/return/approve remain the change vectors.
 */
function SurveyFindings({ review, editMode }) {
    const s = review.survey || {};
    // Wrapped in its own useMemo so the `photosBySection` memo below
    // has a stable dependency (react-hooks/exhaustive-deps: the raw
    // `review.photos || []` logical expression produced a new array
    // every render, invalidating the memo unconditionally).
    const photos = useMemo(() => review.photos || [], [review.photos]);
    const elevations = review.elevations || [];
    const [auditRefreshTick, setAuditRefreshTick] = useState(0);

    const photosBySection = useMemo(() => {
        const map = {};
        for (const p of photos) {
            const k = p.survey_section || "other";
            (map[k] = map[k] || []).push(p);
        }
        return map;
    }, [photos]);

    const photosForElevation = (elevId) => photos.filter((p) => p.elevation_id === elevId);

    return (
        <section data-testid="survey-findings">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-medium text-[#131B2F]">Survey findings</h3>
                <div className="text-xs text-[#4B5565]">
                    {s.submitted_at && <>Submitted {fmt(s.submitted_at)}</>}
                    {s.resubmitted_at && <> · Resubmitted {fmt(s.resubmitted_at)}</>}
                </div>
            </div>

            {/* DEV-010H UAT corrective pass · When Ops toggles Edit survey
                on, render the full-edit panel here (top-of-drawer) so it
                is unmissable — previous UAT showed the panel felt "read-
                only" because it was tucked below the read-only findings. */}
            {editMode && (
                <div className="mb-4 rounded-xl border border-[#DBEAFE] bg-[#EFF6FF] p-3" data-testid="ops-edit-mode-banner">
                    <div className="text-sm font-medium text-[#1E3A8A]">Editing survey</div>
                    <div className="text-xs text-[#1E3A8A] mt-0.5">
                        Every change writes to the survey audit trail.
                        Spec-affecting edits on an approved survey require
                        explicit re-approval before the Installation
                        Specification is versioned.
                    </div>
                </div>
            )}
            {editMode && (
                <OpsEditPanel survey={s} elevations={elevations}
                              onAuditWritten={() => setAuditRefreshTick((n) => n + 1)}
                              onReload={() => setAuditRefreshTick((n) => n + 1)}
                              defaultOpen />
            )}

            <FindingsBlock title="Additional options" testid="findings-addl">
                <KV label="Selected">
                    {(s.additional_options?.selected || []).length
                        ? s.additional_options.selected.map(labelize).join(", ")
                        : "—"}
                </KV>
                {s.additional_options?.notes && <KV label="Notes">{s.additional_options.notes}</KV>}
            </FindingsBlock>

            <FindingsBlock title="Electrical connection" testid="findings-electrical">
                <KV label="Connection point">{labelize(s.electrical?.connection_point)}</KV>
                <KV label="Consumer unit location">
                    {labelize(s.electrical?.consumer_unit_location || s.electrical?.consumer_unit)}
                    {s.electrical?.consumer_unit_location_other && ` — ${s.electrical.consumer_unit_location_other}`}
                </KV>
                <KV label="Distance to consumer unit">{s.electrical?.distance_to_consumer_unit ?? "—"}</KV>
                <KV label="Surge protection">{labelize(s.electrical?.surge_protection)}</KV>
                {s.electrical?.notes && <KV label="Notes">{s.electrical.notes}</KV>}
                <PhotoRow photos={photosBySection["electrical"] || []} />
            </FindingsBlock>

            <FindingsBlock title={`Roof / elevations (${elevations.length})`} testid="findings-elevations" defaultOpen>
                {elevations.length === 0 ? (
                    <div className="text-xs text-[#4B5565]">No elevations captured.</div>
                ) : (
                    <ul className="space-y-3">
                        {elevations.map((e, i) => (
                            <li key={e.id} className="p-3 rounded-lg border border-[#E5E7EB] bg-white" data-testid={`findings-elev-${e.id}`}>
                                <div className="text-sm font-medium text-[#131B2F]">{e.name || `Elevation ${i + 1}`}</div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1 text-xs">
                                    <KV label="Orientation">{e.orientation || "—"}</KV>
                                    <KV label="Roof covering">{labelize(e.roof_covering)}</KV>
                                    <KV label="Proposed qty">{e.proposed_panel_qty ?? "—"}</KV>
                                    <KV label="Surveyed qty">{e.surveyed_panel_qty ?? "—"}</KV>
                                    {e.measurements && <KV label="Measurements">{e.measurements}</KV>}
                                    {e.access_constraints && <KV label="Access constraints">{e.access_constraints}</KV>}
                                    <KV label="Scaffolding required">{labelize(e.scaffolding_required)}</KV>
                                </div>
                                {e.notes && <div className="mt-1 text-xs text-[#131B2F]"><span className="text-[#4B5565]">Notes:</span> {e.notes}</div>}
                                <PhotoRow photos={photosForElevation(e.id)} />
                            </li>
                        ))}
                    </ul>
                )}
            </FindingsBlock>

            <FindingsBlock title="Panel installation" testid="findings-panel">
                <KV label="Method">{labelize(s.panel_installation?.method)}</KV>
                <KV label="Rail colour">{labelize(s.panel_installation?.rail_colour)}</KV>
                {s.panel_installation?.notes && <KV label="Notes">{s.panel_installation.notes}</KV>}
            </FindingsBlock>

            <FindingsBlock title="Cable & conduit" testid="findings-cable">
                <KV label="COPEX">{labelize(s.cable_conduit?.copex)}</KV>
                <KV label="Cable type">{labelize(s.cable_conduit?.cable_type)}</KV>
                {s.cable_conduit?.notes && <KV label="Notes">{s.cable_conduit.notes}</KV>}
                <PhotoRow photos={photosBySection["cable_conduit"] || []} />
            </FindingsBlock>

            <FindingsBlock title="Equipment location" testid="findings-equipment">
                <KV label="Inverter location">{s.equipment_location?.inverter_location || "—"}</KV>
                <KV label="Battery location">{s.equipment_location?.battery_location || "—"}</KV>
                {s.equipment_location?.notes && <KV label="Notes">{s.equipment_location.notes}</KV>}
                <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#4B5565] mb-1">Inverter evidence</div>
                    <PhotoRow photos={photosBySection["equipment_location_inverter"] || (photosBySection["equipment_location"] || []).filter(p => p.evidence_category === "inverter")} />
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#4B5565] mt-2 mb-1">Battery evidence</div>
                    <PhotoRow photos={photosBySection["equipment_location_battery"] || (photosBySection["equipment_location"] || []).filter(p => p.evidence_category === "battery")} />
                </div>
            </FindingsBlock>

            {(s.additional_notes?.text || (photosBySection["additional_notes"] || []).length > 0) && (
                <FindingsBlock title="Additional notes" testid="findings-notes">
                    {s.additional_notes?.text && <div className="text-sm text-[#131B2F] whitespace-pre-wrap">{s.additional_notes.text}</div>}
                    <PhotoRow photos={photosBySection["additional_notes"] || []} />
                </FindingsBlock>
            )}

            <FindingsBlock title="Engineer sign-off" testid="findings-signoff">
                <KV label="Confirmed">{(s.signoff?.confirmed) ? "Yes" : "No"}</KV>
                <KV label="Engineer">{s.signoff?.engineer_name || s.submitted_by?.name || "—"}</KV>
                <KV label="Signed">{fmt(s.signoff?.signed_at || s.submitted_at)}</KV>
            </FindingsBlock>

            <OpsEditPanel survey={s} elevations={elevations}
                          onAuditWritten={() => setAuditRefreshTick((n) => n + 1)}
                          onReload={() => setAuditRefreshTick((n) => n + 1)}
                          hidden={editMode} />
            <OpsAuditPanel surveyId={s.id} refreshTick={auditRefreshTick} />
            {/* Trailing spacer so the sticky action bar never overlays the
                Ops-edit Save button when the drawer scrolls to the end. */}
            <div className="h-20" aria-hidden />
        </section>
    );
}

/**
 * DEV-010H · Ops Edit Panel — full survey edit surface.
 *
 * Operations has full authority to correct any field on the survey.
 * Every material change writes to the survey_audit trail so the
 * correction record survives even after approval. Approved surveys
 * require an explicit `reapprove=true` acknowledgment (backend guard),
 * surfaced here via a confirmation prompt when the survey is approved.
 */
const ADDL_OPS = ["bird_netting", "wifi_booster", "ev_charger", "iboost", "optimisers", "other"];
const CONN_POINTS = ["mains", "consumer_unit", "other"];
const SURGE_OPTS = ["yes", "no", "unsure"];
const CU_OPTS = ["internal", "external", "na", "other"];
const PANEL_METHODS = ["integrated", "on_rails", "other"];
const RAIL_COLOURS = ["black", "silver", "other"];
const COPEX_OPTS = ["yes", "no"];
const CONDUIT_COLOURS = ["white", "black", "other"];
const CABLE_TYPES = ["6mm_twin_earth", "h07", "other"];

function OpsEditPanel({ survey, elevations, onAuditWritten, onReload, defaultOpen, hidden }) {
    // Draft state per section — kept local so we only PATCH on Save.
    const [addl, setAddl] = useState(survey?.additional_options || {});
    const [elec, setElec] = useState(survey?.electrical || {});
    const [pi, setPi]     = useState(survey?.panel_installation || {});
    const [cc, setCc]     = useState(survey?.cable_conduit || {});
    const [eq, setEq]     = useState(survey?.equipment_location || {});
    const [notes, setNotes] = useState(survey?.additional_notes?.text || "");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(null);
    const [openSection, setOpenSection] = useState(defaultOpen ? "additional_notes" : null);

    useEffect(() => {
        setAddl(survey?.additional_options || {});
        setElec(survey?.electrical || {});
        setPi(survey?.panel_installation || {});
        setCc(survey?.cable_conduit || {});
        setEq(survey?.equipment_location || {});
        setNotes(survey?.additional_notes?.text || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [survey?.id, survey?.updated_at]);

    if (!survey?.id) return null;
    if (hidden) return null;
    const approved = survey.status === "approved";

    const savePayload = async (payload, opts = {}) => {
        setSaving(true); setSaved(null);
        try {
            if (approved && opts.spec) {
                if (!window.confirm(
                    "This survey is approved. Saving will version the Approved Installation Specification and require a re-approval. Continue?"
                )) { setSaving(false); return; }
                payload = { ...payload, reapprove: true };
            }
            await api.updateSurvey(survey.id, payload);
            setSaved("saved");
            if (typeof onAuditWritten === "function") onAuditWritten();
            if (typeof onReload === "function") onReload();
        } catch (e) {
            alert(formatApiError(e));
            setSaved("error");
        } finally { setSaving(false); }
    };

    const section = (key, title, spec, body) => (
        <div className="rounded-lg border border-[#E5E7EB] bg-white" data-testid={`ops-edit-section-${key}`}>
            <button type="button"
                    onClick={() => setOpenSection(openSection === key ? null : key)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-[#131B2F]">
                <span>{title}{spec ? " · spec" : ""}</span>
                <span aria-hidden>{openSection === key ? "▾" : "▸"}</span>
            </button>
            {openSection === key && (
                <div className="px-3 pb-3 pt-1 space-y-2">{body}</div>
            )}
        </div>
    );

    return (
        <FindingsBlock title="Operations edit (full survey)" testid="ops-edit-panel" defaultOpen={defaultOpen}>
            <div className="text-[11px] text-[#4B5565] mb-2">
                Every change here writes to the survey audit trail. Edits to spec-relevant
                sections on an approved survey trigger explicit re-approval.
            </div>
            {/* Additional notes — quick fix path (never affects the spec). */}
            {section("additional_notes", "Additional notes", false, (<>
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="ops-edit-notes"
                    className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-sm"/>
                <button type="button"
                    disabled={saving || notes === (survey.additional_notes?.text || "")}
                    onClick={() => savePayload({ additional_notes: { ...(survey.additional_notes || {}), text: notes } })}
                    data-testid="ops-edit-notes-save"
                    className="h-8 px-3 rounded-full text-xs font-medium bg-[#131B2F] text-white disabled:opacity-50">
                    Save notes
                </button>
            </>))}

            {/* Additional options — non-spec. */}
            {section("additional_options", "Additional options", false, (<>
                <div className="flex flex-wrap gap-1.5">
                    {ADDL_OPS.map((o) => {
                        const sel = (addl.selected || []).includes(o);
                        return <button key={o} type="button"
                            onClick={() => setAddl({ ...addl, selected: sel
                                ? (addl.selected || []).filter((x) => x !== o)
                                : [...(addl.selected || []), o] })}
                            data-testid={`ops-edit-addl-${o}`}
                            className={`h-7 px-2.5 rounded-full text-[11px] border ${sel ? "bg-[#131B2F] text-white border-[#131B2F]" : "bg-white text-[#131B2F] border-[#E5E7EB]"}`}>
                            {o}
                        </button>;
                    })}
                </div>
                <input type="text" placeholder="Notes" value={addl.notes || ""}
                    onChange={(e) => setAddl({ ...addl, notes: e.target.value })}
                    data-testid="ops-edit-addl-notes"
                    className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                <button type="button" disabled={saving}
                    onClick={() => savePayload({ additional_options: addl })}
                    data-testid="ops-edit-addl-save"
                    className="h-8 px-3 rounded-full text-xs font-medium bg-[#131B2F] text-white disabled:opacity-50">
                    Save options
                </button>
            </>))}

            {/* Electrical — spec-affecting (partly). */}
            {section("electrical", "Electrical connection", true, (<>
                <ChipRow label="Connection point" value={elec.connection_point}
                    options={CONN_POINTS} testid="ops-edit-elec-cp"
                    onChange={(v) => setElec({ ...elec, connection_point: v })}/>
                <ChipRow label="Consumer unit location" value={elec.consumer_unit_location}
                    options={CU_OPTS} testid="ops-edit-elec-cu"
                    onChange={(v) => setElec({ ...elec, consumer_unit_location: v })}/>
                <ChipRow label="Surge protection" value={elec.surge_protection}
                    options={SURGE_OPTS} testid="ops-edit-elec-surge"
                    onChange={(v) => setElec({ ...elec, surge_protection: v })}/>
                <input type="text" placeholder="Notes" value={elec.notes || ""}
                    onChange={(e) => setElec({ ...elec, notes: e.target.value })}
                    data-testid="ops-edit-elec-notes"
                    className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                <button type="button" disabled={saving}
                    onClick={() => savePayload({ electrical: elec }, { spec: true })}
                    data-testid="ops-edit-elec-save"
                    className="h-8 px-3 rounded-full text-xs font-medium bg-[#131B2F] text-white disabled:opacity-50">
                    Save electrical
                </button>
            </>))}

            {/* Panel installation — spec-affecting. */}
            {section("panel_installation", "Panel installation", true, (<>
                <ChipRow label="Method" value={pi.method}
                    options={PANEL_METHODS} testid="ops-edit-pi-method"
                    onChange={(v) => setPi({ ...pi, method: v })}/>
                <ChipRow label="Rail colour" value={pi.rail_colour}
                    options={RAIL_COLOURS} testid="ops-edit-pi-rail"
                    onChange={(v) => setPi({ ...pi, rail_colour: v })}/>
                <input type="text" placeholder="Notes" value={pi.notes || ""}
                    onChange={(e) => setPi({ ...pi, notes: e.target.value })}
                    data-testid="ops-edit-pi-notes"
                    className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                <button type="button" disabled={saving}
                    onClick={() => savePayload({ panel_installation: pi }, { spec: true })}
                    data-testid="ops-edit-pi-save"
                    className="h-8 px-3 rounded-full text-xs font-medium bg-[#131B2F] text-white disabled:opacity-50">
                    Save panel installation
                </button>
            </>))}

            {/* Cable & conduit — spec-affecting. */}
            {section("cable_conduit", "Cable & conduit", true, (<>
                <ChipRow label="Cable type" value={cc.cable_type}
                    options={CABLE_TYPES} testid="ops-edit-cc-type"
                    onChange={(v) => setCc({ ...cc, cable_type: v })}/>
                <ChipRow label="COPEX" value={cc.copex_required}
                    options={COPEX_OPTS} testid="ops-edit-cc-copex"
                    onChange={(v) => setCc({ ...cc, copex_required: v })}/>
                <ChipRow label="Conduit colour" value={cc.conduit_colour}
                    options={CONDUIT_COLOURS} testid="ops-edit-cc-colour"
                    onChange={(v) => setCc({ ...cc, conduit_colour: v })}/>
                <input type="text" placeholder="Notes" value={cc.notes || ""}
                    onChange={(e) => setCc({ ...cc, notes: e.target.value })}
                    data-testid="ops-edit-cc-notes"
                    className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                <button type="button" disabled={saving}
                    onClick={() => savePayload({ cable_conduit: cc }, { spec: true })}
                    data-testid="ops-edit-cc-save"
                    className="h-8 px-3 rounded-full text-xs font-medium bg-[#131B2F] text-white disabled:opacity-50">
                    Save cable & conduit
                </button>
            </>))}

            {/* Equipment location — spec-affecting. */}
            {section("equipment_location", "Equipment location", true, (<>
                <input type="text" placeholder="Proposed inverter location"
                    value={eq.proposed_inverter_location || ""}
                    onChange={(e) => setEq({ ...eq, proposed_inverter_location: e.target.value })}
                    data-testid="ops-edit-eq-inverter"
                    className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                <input type="text" placeholder="Notes" value={eq.notes || ""}
                    onChange={(e) => setEq({ ...eq, notes: e.target.value })}
                    data-testid="ops-edit-eq-notes"
                    className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                <button type="button" disabled={saving}
                    onClick={() => savePayload({ equipment_location: eq }, { spec: true })}
                    data-testid="ops-edit-eq-save"
                    className="h-8 px-3 rounded-full text-xs font-medium bg-[#131B2F] text-white disabled:opacity-50">
                    Save equipment location
                </button>
            </>))}

            {/* Elevations — spec-affecting; add/edit/remove inline. */}
            {section("elevations", "Roof / elevations", true, (
                <OpsElevationsEditor surveyId={survey.id} elevations={elevations}
                    approved={approved} onChange={() => { onAuditWritten?.(); onReload?.(); }}/>
            ))}

            <div className="mt-2 flex items-center gap-2">
                {saved === "saved" && <span className="text-xs text-[#166534]" data-testid="ops-edit-saved">Saved · audit recorded</span>}
                {saved === "error" && <span className="text-xs text-[#B91C1C]">Save failed</span>}
                {saving && <span className="text-xs text-[#4B5565]"><Loader2 size={12} className="inline animate-spin mr-1"/>Saving…</span>}
            </div>
        </FindingsBlock>
    );
}

function ChipRow({ label, value, options, testid, onChange }) {
    return (
        <div>
            <div className="text-[10px] text-[#4B5565] mb-1">{label}</div>
            <div className="flex flex-wrap gap-1.5" data-testid={testid}>
                {options.map((o) => (
                    <button key={o} type="button" onClick={() => onChange(o)}
                        className={`h-7 px-2.5 rounded-full text-[11px] border ${value === o ? "bg-[#131B2F] text-white border-[#131B2F]" : "bg-white text-[#131B2F] border-[#E5E7EB]"}`}>
                        {o}
                    </button>
                ))}
            </div>
        </div>
    );
}

function OpsElevationsEditor({ surveyId, elevations, approved, onChange }) {
    const [busy, setBusy] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState({});
    const [newName, setNewName] = useState("");
    const [newOrient, setNewOrient] = useState("S");
    const [newRoof, setNewRoof] = useState("slate");
    const [newQty, setNewQty] = useState(1);

    const openEdit = (e) => {
        setEditingId(e.id);
        setEditDraft({
            name: e.name || "",
            orientation: e.orientation || "S",
            roof_covering: e.roof_covering || "slate",
            surveyed_panel_qty: e.surveyed_panel_qty || 0,
            notes: e.notes || "",
        });
    };
    const saveEdit = async () => {
        if (!editingId) return;
        setBusy(editingId);
        try {
            await api.updateElevation(editingId, {
                ...editDraft,
                ...(approved ? { reapprove: true } : {}),
            });
            setEditingId(null); setEditDraft({});
            onChange?.();
        } catch (e) {
            alert(formatApiError(e));
        } finally { setBusy(null); }
    };

    const add = async () => {
        if (!newName.trim()) return;
        setBusy("add");
        try {
            const payload = {
                name: newName.trim(),
                orientation: newOrient,
                roof_covering: newRoof,
                surveyed_panel_qty: Number(newQty) || 0,
                ...(approved ? { reapprove: true } : {}),
            };
            await api.createElevation(surveyId, payload);
            setNewName("");
            onChange?.();
        } catch (e) {
            alert(formatApiError(e));
        } finally { setBusy(null); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this elevation? An audit entry will be recorded.")) return;
        setBusy(id);
        try {
            await api.deleteElevation(id, approved ? { reapprove: true } : undefined);
            onChange?.();
        } catch (e) {
            alert(formatApiError(e));
        } finally { setBusy(null); }
    };

    return (
        <div className="space-y-2" data-testid="ops-edit-elevations">
            {elevations.length === 0 && <div className="text-[11px] text-[#4B5565]">No elevations captured.</div>}
            {elevations.map((e) => (
                <div key={e.id} className="p-2 rounded border border-[#E5E7EB]"
                     data-testid={`ops-edit-elev-${e.id}`}>
                    {editingId === e.id ? (
                        <div className="space-y-2">
                            <input type="text" value={editDraft.name}
                                onChange={(ev) => setEditDraft({ ...editDraft, name: ev.target.value })}
                                data-testid={`ops-edit-elev-edit-name-${e.id}`}
                                className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                            <div className="grid grid-cols-2 gap-2">
                                <select value={editDraft.orientation}
                                    onChange={(ev) => setEditDraft({ ...editDraft, orientation: ev.target.value })}
                                    className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs">
                                    {["N","NE","E","SE","S","SW","W","NW"].map((o) => <option key={o}>{o}</option>)}
                                </select>
                                <select value={editDraft.roof_covering}
                                    onChange={(ev) => setEditDraft({ ...editDraft, roof_covering: ev.target.value })}
                                    className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs">
                                    {["slate","rosemary","concrete","other"].map((o) => <option key={o}>{o}</option>)}
                                </select>
                            </div>
                            <input type="number" min="0" value={editDraft.surveyed_panel_qty}
                                onChange={(ev) => setEditDraft({ ...editDraft, surveyed_panel_qty: Number(ev.target.value) || 0 })}
                                className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                            <input type="text" placeholder="Notes" value={editDraft.notes}
                                onChange={(ev) => setEditDraft({ ...editDraft, notes: ev.target.value })}
                                className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                            <div className="flex gap-2">
                                <button type="button" onClick={saveEdit} disabled={busy === e.id}
                                    data-testid={`ops-edit-elev-save-${e.id}`}
                                    className="h-7 px-2.5 rounded-full text-[11px] bg-[#131B2F] text-white disabled:opacity-50">
                                    Save
                                </button>
                                <button type="button" onClick={() => { setEditingId(null); setEditDraft({}); }}
                                    className="h-7 px-2.5 rounded-full text-[11px] bg-white text-[#131B2F] border border-[#E5E7EB]">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between">
                            <div className="text-xs">
                                <b>{e.name}</b> · {e.orientation} · {e.roof_covering} · {e.surveyed_panel_qty} panels
                            </div>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={() => openEdit(e)}
                                    data-testid={`ops-edit-elev-edit-${e.id}`}
                                    className="text-[11px] text-[#131B2F] hover:underline">
                                    Edit
                                </button>
                                <button type="button" onClick={() => remove(e.id)} disabled={busy === e.id}
                                    data-testid={`ops-edit-elev-delete-${e.id}`}
                                    className="text-[11px] text-[#B91C1C] hover:underline">
                                    Delete
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#E5E7EB]">
                <input type="text" placeholder="New elevation name" value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    data-testid="ops-edit-elev-new-name"
                    className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                <select value={newOrient} onChange={(e) => setNewOrient(e.target.value)}
                    data-testid="ops-edit-elev-new-orient"
                    className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs">
                    {["N","NE","E","SE","S","SW","W","NW"].map((o) => <option key={o}>{o}</option>)}
                </select>
                <select value={newRoof} onChange={(e) => setNewRoof(e.target.value)}
                    className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs">
                    {["slate","rosemary","concrete","other"].map((o) => <option key={o}>{o}</option>)}
                </select>
                <input type="number" min="0" placeholder="Panels" value={newQty}
                    onChange={(e) => setNewQty(e.target.value)}
                    className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
            </div>
            <button type="button" disabled={busy === "add" || !newName.trim()}
                onClick={add} data-testid="ops-edit-elev-add"
                className="h-8 px-3 rounded-full text-xs font-medium bg-[#131B2F] text-white disabled:opacity-50">
                Add elevation
            </button>
        </div>
    );
}

function ClarificationTargetsPicker({ targets, onChange, survey, elevations }) {
    const SECTIONS = [
        { key: "additional_options", label: "Additional options" },
        { key: "electrical",         label: "Electrical connection" },
        { key: "roof_elevations",    label: "Roof / elevations" },
        { key: "panel_installation", label: "Panel installation" },
        { key: "cable_conduit",      label: "Cable & conduit" },
        { key: "equipment_location", label: "Equipment location" },
        { key: "additional_notes",   label: "Additional notes" },
    ];
    const [section, setSection] = useState("");
    const [field, setField] = useState("");
    const [elevationId, setElevationId] = useState("");
    const add = () => {
        if (!section) return;
        const label = [SECTIONS.find((s) => s.key === section)?.label,
                       field, elevationId ? elevations.find((e) => e.id === elevationId)?.name : null]
                     .filter(Boolean).join(" · ");
        onChange([...(targets || []), { section, field: field || undefined,
                                        elevation_id: elevationId || undefined, label }]);
        setSection(""); setField(""); setElevationId("");
    };
    const remove = (i) => onChange((targets || []).filter((_, idx) => idx !== i));
    return (
        <div className="mt-3 pt-3 border-t border-[#E5E7EB]" data-testid="clar-targets-picker">
            <div className="text-xs font-medium text-[#131B2F] mb-1">Pinpoint (optional)</div>
            {(targets || []).length > 0 && (
                <ul className="flex flex-wrap gap-1 mb-2">
                    {targets.map((t, i) => (
                        <li key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#1E3A8A] border border-[#C7D2FE] flex items-center gap-1">
                            {t.label}
                            <button type="button" onClick={() => remove(i)}
                                    data-testid={`clar-target-remove-${i}`}
                                    className="text-[#1E3A8A] hover:text-[#B91C1C]">×</button>
                        </li>
                    ))}
                </ul>
            )}
            <div className="grid grid-cols-3 gap-2">
                <select value={section} onChange={(e) => setSection(e.target.value)}
                        data-testid="clar-target-section"
                        className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs">
                    <option value="">Section…</option>
                    {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <input type="text" placeholder="Field (optional)"
                       value={field} onChange={(e) => setField(e.target.value)}
                       data-testid="clar-target-field"
                       className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"/>
                <select value={elevationId} onChange={(e) => setElevationId(e.target.value)}
                        data-testid="clar-target-elevation"
                        className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs">
                    <option value="">Elevation (optional)</option>
                    {(elevations || []).map((e) => <option key={e.id} value={e.id}>{e.name || "Elevation"}</option>)}
                </select>
            </div>
            <button type="button" onClick={add} disabled={!section}
                    data-testid="clar-target-add"
                    className="mt-2 h-8 px-3 rounded-full text-xs bg-white border border-[#131B2F] text-[#131B2F] disabled:opacity-50">
                Add target
            </button>
        </div>
    );
}

function OpsAuditPanel({ surveyId, refreshTick }) {
    const [entries, setEntries] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        if (!surveyId) return;
        let cancelled = false;
        (async () => {
            try {
                const body = await api.getSurveyAudit(surveyId);
                if (!cancelled) setEntries(body.entries || []);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || "Could not load audit trail.");
            }
        })();
        return () => { cancelled = true; };
    }, [surveyId, refreshTick]);

    if (err) {
        return (
            <FindingsBlock title="Ops edit audit trail" testid="ops-audit-panel">
                <div className="text-xs text-[#B91C1C]">{err}</div>
            </FindingsBlock>
        );
    }
    return (
        <FindingsBlock title={`Ops edit audit trail (${entries?.length ?? "…"})`} testid="ops-audit-panel">
            {entries === null && (
                <div className="text-xs text-[#4B5565]"><Loader2 size={12} className="inline-block animate-spin mr-1" />Loading…</div>
            )}
            {entries && entries.length === 0 && (
                <div className="text-xs text-[#4B5565]">No Ops edits recorded.</div>
            )}
            {entries && entries.length > 0 && (
                <ul className="space-y-2" data-testid="ops-audit-list">
                    {entries.map((row) => (
                        <li key={row.id} className="rounded-md border border-[#E5E7EB] bg-white p-2 text-xs">
                            <div className="text-[#131B2F] font-medium">
                                {row.actor_display_name || "Operations"} · {row.survey_status_at_edit}
                            </div>
                            <div className="text-[10px] text-[#4B5565]">{fmt(row.at)}</div>
                            <ul className="mt-1 space-y-0.5">
                                {(row.entries || []).map((e, i) => (
                                    <li key={i} className="text-[#4B5565]">
                                        <span className="text-[#131B2F]">{e.path}</span>:
                                        {" "}
                                        <span className="line-through">{JSON.stringify(e.before)}</span>
                                        {" → "}
                                        <span className="text-[#166534]">{JSON.stringify(e.after)}</span>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
            )}
        </FindingsBlock>
    );
}

function labelize(v) {
    if (v === null || v === undefined || v === "") return "—";
    return String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function KV({ label, children }) {
    return (
        <div className="text-xs">
            <span className="text-[#4B5565]">{label}: </span>
            <span className="text-[#131B2F]">{children}</span>
        </div>
    );
}

function FindingsBlock({ title, children, testid, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="mt-2 rounded-lg border border-[#E5E7EB] bg-[#FAFBFC]" data-testid={testid}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left"
                data-testid={`${testid}-toggle`}
                aria-expanded={open}
            >
                {open ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
                <span className="text-sm font-medium text-[#131B2F]">{title}</span>
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-1.5">
                    {children}
                </div>
            )}
        </div>
    );
}

function PhotoRow({ photos }) {
    if (!photos || photos.length === 0) return null;
    // DEV-010H UAT corrective pass 2 · Never fall back to a URL without
    // a `?dt=` token — that would guarantee a 401 broken thumbnail.
    // The backend always mints a viewer-scoped token in `_build_review`,
    // so a missing `download_url` here indicates a genuine data problem
    // and we surface a placeholder rather than a broken image icon.
    return (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="findings-photo-row">
            {photos.map((p) => {
                const url = p.download_url
                    ? `${process.env.REACT_APP_BACKEND_URL}${p.download_url}`
                    : null;
                if (!url) {
                    return (
                        <div
                            key={p.id}
                            className="w-24 h-24 rounded-lg border border-dashed border-[#E5E7EB] bg-[#F9FAFB] text-[10px] text-[#4B5565] flex items-center justify-center text-center px-1"
                            data-testid={`findings-photo-missing-${p.id}`}
                            title="Photo unavailable — reload the review to refresh access."
                        >
                            Photo unavailable
                        </div>
                    );
                }
                return (
                    <a
                        key={p.id}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="block"
                        title={p.title || "Survey photo"}
                        data-testid={`findings-photo-${p.id}`}
                    >
                        <img
                            src={url}
                            alt={p.title || "Survey photo"}
                            loading="lazy"
                            className="w-24 h-24 rounded-lg object-cover border border-[#E5E7EB] bg-[#F1F5F9] hover:opacity-90"
                        />
                    </a>
                );
            })}
        </div>
    );
}

